// @flow

import * as AppSubscriber from 'app/entities/AppSubscriber';
import * as CliAppCollaborator from 'app/entities/CliAppCollaborator';
import * as CliAppInvitee from 'app/entities/CliAppInvitee';
import ErrorActions from 'app/error/ErrorActions';
import { notify } from 'app/developer-v3/store/shared/actions';
import { NEW_ENTITY_IDS, ROLES } from 'app/developer-v3/utils/constants';

import * as Data from './Data';
import ActionTypes from './ActionTypes';

import type { Dispatch, GetState } from 'app/common/types';

type MemberProps = {
  appVersion?: string,
  email: string,
  id?: string,
  message?: string,
};

export const submitNewTeamMember = (props?: {}) => {
  return { type: ActionTypes.SUBMIT_NEW_TEAM_MEMBER, teamMember: props };
};

export const submitNewTeamMemberDone = ({ message }: { message: string }) => {
  return (dispatch: Dispatch) => {
    dispatch({ type: ActionTypes.RESET_NEW_TEAM_MEMBER_FORM });
    dispatch({ type: ActionTypes.SUBMIT_NEW_TEAM_MEMBER_DONE, message });
  };
};

export const submitNewTeamMemberFail = (error: string) => {
  return { type: ActionTypes.SUBMIT_NEW_TEAM_MEMBER_FAIL, error };
};

export const updateAddForm = (role: $Keys<typeof ROLES>, value: string) => {
  const actionType =
    role === ROLES.collaborator
      ? ActionTypes.UPDATE_CLI_ADD_COLLABORATOR_FORM
      : ActionTypes.UPDATE_CLI_ADD_INVITEE_FORM;

  return {
    type: actionType,
    fieldName: 'email',
    value,
  };
};

export const addEntity = (
  dispatch: Dispatch,
  getState: GetState,
  entity,
  entityProps: MemberProps,
  role: $Keys<typeof ROLES>
) => {
  dispatch(submitNewTeamMember());

  const entityId =
    role === ROLES.collaborator
      ? NEW_ENTITY_IDS.collaborator
      : NEW_ENTITY_IDS.invitee;

  const finalEntityProps = {
    ...entityProps,
    role,
    status: 'waiting',
    id: entityId,
  };

  dispatch(
    entity.actions.createCollectionEntity(finalEntityProps, {
      isPersisted: false,
    })
  );

  dispatch(entity.actions.createEntity(null, entityId)).then(() => {
    const state = getState();
    const createdEntity = entity.selectors.all.entity(entityId, state);
    const isFailure = entity.selectors.isFailure(createdEntity);

    if (isFailure) {
      const statusMessage = entity.selectors.statusMessage(createdEntity);

      dispatch(submitNewTeamMemberFail(statusMessage));
      return dispatch(ErrorActions.clearErrors());
    }
    dispatch(updateAddForm(role, ''));
    return dispatch(
      submitNewTeamMemberDone({
        message: `Invitation email sent to ${entityProps.email}.`,
      })
    );
  });
};

export const addCliInvitee = (
  appId: string | number,
  email: string,
  appVersion: string
) => {
  return (dispatch: Dispatch, getState: GetState) =>
    addEntity(
      dispatch,
      getState,
      CliAppInvitee,
      { email, app_version: appVersion },
      ROLES.invitee
    );
};

// For invitees, collaborators, and subscribers
export const deleteEntity = (entity, entityProps: MemberProps) => (
  dispatch: Dispatch,
  getState: GetState
) =>
  dispatch(entity.actions.deleteEntity(null, entityProps.id)).then(() => {
    const state = getState();
    const fetchedEntity = entity.selectors.all.entity(entityProps.id, state);
    const isFailure = entity.selectors.isFailure(fetchedEntity);

    // Only notify of failure if it failed
    if (isFailure) {
      const statusMessage = entity.selectors.statusMessage(fetchedEntity);
      return dispatch(notify.failure(statusMessage));
    }
    return dispatch(
      notify.success(`${entityProps.email} has been removed from your team.`)
    );
  });

export const deleteCliInvitee = (
  appId: string | number,
  inviteeProps: MemberProps
) => deleteEntity(CliAppInvitee, inviteeProps);

export const loadCliInvitees = () => {
  return (dispatch: Dispatch) => {
    dispatch(CliAppInvitee.actions.resetCollection());
    dispatch(CliAppInvitee.actions.loadCollection());
  };
};

export const loadSubscribers = () => {
  return (dispatch: Dispatch) => {
    dispatch(AppSubscriber.actions.resetAndLoadCollection());
  };
};

export const addSubscriber = (
  appId: string | number,
  email: string,
  message: string
) => {
  return (dispatch: Dispatch, getState: GetState) =>
    addEntity(
      dispatch,
      getState,
      AppSubscriber,
      { email, message, is_wb_app: false },
      ROLES.subscriber
    );
};

export const deleteSubscriber = (
  appId: string | number,
  subscriberProps: MemberProps
) => deleteEntity(AppSubscriber, subscriberProps);

export const loadCliCollaborators = () => {
  return (dispatch: Dispatch) => {
    dispatch(CliAppCollaborator.actions.resetCollection());
    dispatch(CliAppCollaborator.actions.loadCollection());
  };
};

export const deleteCliCollaborator = (
  appId: string | number,
  collaboratorProps: MemberProps
) => deleteEntity(CliAppCollaborator, collaboratorProps);

export const addCliCollaborator = (
  appId: string | number,
  email: string,
  message: string
) => {
  return (dispatch: Dispatch, getState: GetState) =>
    addEntity(
      dispatch,
      getState,
      CliAppCollaborator,
      { email, message },
      ROLES.collaborator
    );
};

export const clearMessage = (id: string) => ({
  type: ActionTypes.CLEAR_MESSAGE,
  payload: { id },
});

export const resetNewTeamMemberForm = () => ({
  type: ActionTypes.RESET_NEW_TEAM_MEMBER_FORM,
});

export const updateNewTeamMember = (field: string, value: string) => {
  return { type: ActionTypes.UPDATE_NEW_TEAM_MEMBER, field, value };
};

const getFormattedDateForLogs = (addMinutes = 0) =>
  new Date(new Date().getTime() + addMinutes * 60000)
    .toISOString()
    .substr(0, 17) + '00+00:00';

export const getTestLogs = (
  logsType: 'http' | 'console' | 'bundle',
  appId: number,
  versionNumber: string
) => {
  const filters = {
    start_date: getFormattedDateForLogs(-0.25),
    end_date: getFormattedDateForLogs(5),
    personal: false,
    category: logsType,
    app_version: versionNumber,
    limit: 10,
  };

  return {
    type: ActionTypes.GET_TEST_LOGS,
    promise: Data.loadAppEvents(appId, filters),
    resultKey: 'events',
    logsType,
  };
};
